import { createIsomorphicFn } from '@tanstack/react-start';

import type { ClientOptions } from '../api/core-api/types.gen';

import {
  createClient,
  createConfig,
  type Client,
} from '../api/core-api/client';
import { buildCoreApiHeaders, getCoreApiUrl } from './server-fetch';

/**
 * Returns the appropriate API client based on environment:
 * - Server: serverClient with direct core-api URL + auth headers
 * - Browser: undefined (SDK falls back to default browser client with v4 proxy)
 *
 * Usage:
 * ```ts
 * const response = await someApiCall({
 *   client: getClient(userId),
 *   path: { id },
 * })
 * ```
 */
export const getClient = createIsomorphicFn()
  .server((userId?: string) => createServerClient(userId))
  .client((): undefined => {});

// Helper to keep typescript happy when using getClient() in a server handler
export const assertClient = (client?: Client) => {
  if (!client) throw new Error('Client is not defined');
};

/**
 * Create a client configured for server-side API calls.
 * Includes auth headers, client credentials, and WAF keys.
 */
function createServerClient(userId?: string) {
  return createClient(
    createConfig<ClientOptions>({
      baseURL: getCoreApiUrl(),
      headers: buildCoreApiHeaders(userId) as Record<string, string>,
    }),
  );
}
